import Backbone from 'backbone'
import _ from 'lodash'
import * as AggregationTypeOptions from 'scoreboard/datasets/aggregation-type-options'
const { sum, average, minimum, maximum } = AggregationTypeOptions
import dropdown from 'scoreboard/components/dropdown'
import i18n from 'scoreboard/components/i18n'
import formHelper from 'scoreboard/components/form-helper'
import FormView from 'scoreboard/components/form-view'
import unwrapModel from 'scoreboard/components/unwrap-model'
import selects from 'scoreboard/components/selects'
import metadata from 'scoreboard/components/metadata'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import numericValidator from 'scoreboard/components/validators/numeric-validator'
import DataType from 'scoreboard/datasets/model/data-type'
const { PERCENT } = DataType
import * as dataProperty from '../../data-property'
import SerieSelect from '../serie-select'
import template from './lines-and-bands-data-source.hbs'
import updateLineDataSource from './update-line-data-source'
import LinesAndBands from './lines-and-bands'
import FeatureMetadata from '../feature-metadata'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import getAxisDataType from './get-axis-data-type'

/**
 * @property {ChartLine|ChartFill} model
 * @property {LinesAndBands} labFeature
 * @property {ChartDesigner} chart
 * @property {getMetadata} getMetadata
 * @property {getLineSourceTypes} getLineSourceTypes
 *
 * @type {LinesAndBandsDataSource}
 */
const LinesAndBandsDataSource = Backbone.View.extend({

	tagName: 'form',

	className: 'lines-and-bands-data-source',

	events: {
		'change [name=lineSourceType]': 'onChangeLineSourceType',
		'change [name=serieSource]': 'onChangeSerie'
	},

	/**
	 *
	 * @param {ChartDesigner} options.chart
	 */
	initialize: function(options = {}) {
		this.options = options
		this.chart = options.chart
		this.labFeature = options.labFeature

		this.formView = new FormView({
			el: this.el,
			submit: this.save.bind(this),
			allowCleanSubmission: true
		})
	},

	render() {
		this.$el.empty().append(template({

		}))

		this.fieldLabel = this.$('.field .label-text')
		this.fieldSelect = this.$('.field select')
		this.aggregationSelect = this.$('.aggregation select')
		selects(this.$('select'))

		formHelper.writeToDom(this.model, this.el)

		this.markFields()
		this.renderLineSourceSelect()
		this.onChangeLineSourceType()

		this.formView.start()

		return this
	},

	/**
	 * "Line Data Source" - This is the first selection the user makes
	 */
	renderLineSourceSelect: function () {
		const {lineSourceType} = unwrapModel(this.model)
		const {CONSTANT, SCORECARD_ITEM, INITIATIVE,
			AGGREGATION, AGGREGATION_PERCENTAGE} = getLineSourceTypes()

		let options = [
			{value: CONSTANT, label: i18n.get('charts.chart.constantNumber')}
		]

		if (this.chart.hasScorecardItemSources()) {
			options.push({
				value: SCORECARD_ITEM,
				label: i18n.get('charts.dataSource.itemField', metadata.scorecards.singularCode)
			})
		}
		if (this.chart.hasInitiativeSources()) {
			options.push({
				value: INITIATIVE,
				label: i18n.get('charts.dataSource.itemField', metadata.initiatives.singularCode)
			})
		}

		options.push({
			value: AGGREGATION,
			label: i18n.get('charts.dataSource.aggregated')
		})
		options.push({
			value: AGGREGATION_PERCENTAGE,
			label: i18n.get('common.percentage.of', 'charts.dataSource.aggregated')
		})


		this.lineSourceSelect = this.$('select[name=lineSourceType]')
		selects(this.lineSourceSelect, {
			options
		})
		selects.val(this.lineSourceSelect, lineSourceType)
	},

	markFields: function() {
		this.markedPercentage = new MarkedField({
			el: this.$('.percentage input'),
			validators: [requiredValidator, numericValidator],
			dataType: PERCENT
		}).render()

		this.markedConstant = new MarkedField({
			el: this.$('.constant-number input'),
			validators: [requiredValidator, numericValidator],
			evaluator: numericEvaluator,
			dataType: getAxisDataType({
				chart: this.chart,
				axis: this.model.get('axis')
			})
		}).render()

		this._markedFields = [this.markedPercentage, this.markedConstant]
	},

	onChangeLineSourceType: function(e) {
		const type = this.lineSourceSelect.val()
		this._updateFieldSelect(type)
		this._updateAggregationFields(type)
		this._updateConstantValueInput(type)
	},

	onChangeSerie: function(e) {
		this._updateAggregationSelect()
	},

	_updateFieldSelect: function(dataSourceType) {
		const showFieldSelector = requiresSerie(dataSourceType)
		this.$('.field').toggleClass('is-not-displayed', !showFieldSelector)

		if (showFieldSelector) {
			this.fieldLabel.text(i18n.get('charts.dataSource.itemField',
				metadata[dataSourceType].singularCode))

			this.fieldOptions = this.getFieldOptions(dataSourceType)
			selects(this.fieldSelect.empty())
			const {fieldName} = unwrapModel(this.model)
			const {SCORECARD_ITEM} = getLineSourceTypes()
			if (dataSourceType == SCORECARD_ITEM) {
				// Thresholds for multiple scoring types complicate things here
				// see _readScorecardFieldNameSelection
				dropdown.idLabels(this.fieldSelect, this.fieldOptions, undefined, {
					selectedLabel: fieldName
				})
			} else {
				dropdown.idLabels(this.fieldSelect, this.fieldOptions)
				// Unless the field is among the options just select the 1st one
				if (_.find(this.fieldOptions, o => o.id === fieldName)) {
					selects.val(this.fieldSelect, fieldName)
				}
			}
		}
	},

	/**
	 * Hides or shows the aggregation options in the form based on the line
	 * source selection.
	 *
	 * Allows a serie from the chart to be selected, or an "All Series" option
	 * @param dataSourceType The data source type the user has selected.
	 */
	_updateAggregationFields: function(dataSourceType) {
		const { AGGREGATION_PERCENTAGE } = getLineSourceTypes()
		const { serieSourceId, serieSourceTempId } = unwrapModel(this.model)
		const { axis } = unwrapModel(this.labFeature)

		const supportsAggregation = this._supportsAggregation()
		// Series Select
		this.serieSelectEl = this.$('.serie')
		this.serieSelectEl.toggleClass('is-not-displayed', !supportsAggregation)
		if (supportsAggregation) {
			this.serieSourceSelect && this.serieSourceSelect.remove()

			this.serieSourceSelect = SerieSelect.appendAndRender(
				this.serieSelectEl,
				new SerieSelect({
					model: this.chart,
					name: 'serieSource',
					axis,
					includeAll: true,
					filter: s => this._includeSerieInSelect(s, dataSourceType),
					selected: {
						id: serieSourceId,
						tempId: serieSourceTempId
					}
				}))
		}

		// Aggregation Type Select
		this._updateAggregationSelect()

		// Percentage Input
		const showPercentage = dataSourceType === AGGREGATION_PERCENTAGE
		this.$('.percentage').toggleClass('is-not-displayed', !showPercentage)
	},

	_supportsAggregation: function() {
		const { CONSTANT } = getLineSourceTypes()
		const { PER_BREAKDOWN1} = getMetadata().scopes
		const { scope, axis } = unwrapModel(this.labFeature)
		const dataSourceType = this.lineSourceSelect.val()

		return CONSTANT !== dataSourceType &&
			_.size(this.chart.getFeatures({ type: 'serie', axis })) > 1 && // #45797
			(isAggregationSource(dataSourceType) ||
				scope === PER_BREAKDOWN1) // #40403 for chart upgrades to work
	},

	_updateAggregationSelect: function() {
		const { aggregationType } = unwrapModel(this.model)
		const show = this._supportsAggregation() && this.serieSourceSelect.isAll()
		this.$('.aggregation').toggleClass('is-not-displayed', !show)
		selects(this.aggregationSelect.empty())
		const aggregationOptions = [sum, average, minimum, maximum]
		dropdown.idLabels(this.aggregationSelect, aggregationOptions, aggregationType)
	},

	_includeSerieInSelect: function(serie, dataSourceType) {
		const {isAggregation} = getLineSourceTypes()
		const {domainType} = unwrapModel(serie).source || {}
		return isAggregation(dataSourceType) || domainType === dataSourceType
	},

	_updateConstantValueInput: function(dataSourceType) {
		const {CONSTANT} = getLineSourceTypes()
		const showInput = _.isNil(dataSourceType) || CONSTANT === dataSourceType
		this.$('.constant-number').toggleClass('is-not-displayed', !showInput)
	},

	/**
	 * Get the distinct options for a type of serie (scorecard|initiative) on
	 * the chart
	 * @param {String} domainType
	 */
	getFieldOptions: function(domainType) {
		const section = metadata[domainType].section
		return _(this.chart.features.featuresAndChildren())
			.filter(f => _.get(f, 'attributes.source.domainType') === domainType)
			.flatMap(s => {
				const id = s.getGenericDataSource().domainObjectId
				return dataProperty.options({
					domainType,
					domainObject: this.chart.data.domainObject(section, id),
					includeGoalAlias: true
				})
			})
			.uniqBy('label')
			.sortBy(field => i18n.get(field.label).toLocaleUpperCase())
			.value()
	},

	save: function() {
		const {SCORECARD_ITEM} = getLineSourceTypes()
		// form values:
		// lineSourceType, constantNumber, percentage, serieSourceTempId, aggregationType, fieldName
		const data = formHelper.readFromDom(this.el)
		if (data.lineSourceType === SCORECARD_ITEM) {
			data.fieldName = this._readScorecardFieldNameSelection()
		}
		this._autoChangeScope(data.lineSourceType)
		updateLineDataSource(this.model, data, this.getSerieSourceIds())

		this.trigger('done')
	},

	getSerieSourceIds: function () {
		if (this.serieSourceSelect) {
			return this.serieSourceSelect.val()
		} else {
			return {}
		}
	},

	_readScorecardFieldNameSelection() {
		// Must derive the (untranslated) label from the options
		// the option ids are thresholdN and will be duplicated if there are
		// heterogeneous scoring types represented in the select.
		const {SCORECARD_ITEM} = getLineSourceTypes()
		return SCORECARD_ITEM === this.lineSourceSelect.val() ?
			this.fieldOptions[this.fieldSelect.prop('selectedIndex')].label :
			undefined
	},

	/**
	 * Sets the lines and bands feature level scope automatically based on the
	 * data source the user just selected, if possible. See #39533 for details.
	 *
	 * @param dataSourceType
	 */
	_autoChangeScope(dataSourceType) {
		const {PER_SERIES, PER_BREAKDOWN1, ENTIRE_CHART} = getMetadata().scopes
		const {scope} = unwrapModel(this.labFeature)
		let newScope
		if (scope === PER_BREAKDOWN1) {
			newScope = scope
		} else if (requiresSerie(dataSourceType) && scope !== PER_SERIES) {
			newScope = this._computeNewScope(PER_SERIES)
		} else if (_.isNil(scope) || scope === PER_SERIES) {
			// If the scope is already any of the other options it is compatible
			// with CONSTANT, and AGGREGATE options.
			newScope = this._computeNewScope(ENTIRE_CHART)
		}
		return newScope
	},

	/**
	 * If new scope doesn't match the current scope and it doesn't conflict with
	 * any current lines, automatically change the scope.  But if it does
	 * conflict with a current line, leave the scope as-is.
	 *
	 * @param requiredScope The scope that must be set to comply with the newly
	 * selected data source for a line.
	 */
	_computeNewScope(requiredScope) {
		const {scope: currentScope} = unwrapModel(this.labFeature)
		let newScope = currentScope
		if (requiredScope !== currentScope) {
			const {PER_SERIES, ENTIRE_CHART} = getMetadata().scopes
			const lines = this.labFeature.getChildren({type: 'line'})
			const {id: modelId, tempId: modelTempId} = this.model.attributes
			const scopeLocked = _.some(lines, ({id, lineSourceType}) => {
				const lineRequiresPerSeriesScope = requiresSerie(lineSourceType)
				// Can't change from per series for scorecard/initiative sources
				if (currentScope === PER_SERIES && lineRequiresPerSeriesScope) {
					return true
				}
				if (id == (modelId || modelTempId)) {
					return false // Don't consider the line that is being edited
				}
				// Can't change to per series for aggregations
				if (requiredScope === PER_SERIES && isAggregationSource(lineSourceType)) {
					return true
				}
			})

			if (!scopeLocked) {
				newScope = requiredScope
				// this is too chatty for our tests
				//console.log('Automatically setting reference line and band scope to:', newScope, 'from', currentScope)
				this.labFeature.set('scope', newScope)
			}
		}
		return newScope
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([...this._markedFields, this.formView, this.serieSourceSelect], 'remove')
	}

})

function getLineSourceTypes() {
	const {lineSourceTypes} = getMetadata()
	return lineSourceTypes
}

function getMetadata() {
	return FeatureMetadata[LinesAndBands.FEATURE_KEY]
}

/**
 * @param {String} lineSourceType
 *
 * @returns {boolean} true if the lineSourceType requires that the scope be
 * "Data Item" (series) and thus mandates a secondary select for the
 * scorecard/initiative field.
 */
function requiresSerie(lineSourceType) {
	const {SCORECARD_ITEM, INITIATIVE} = getLineSourceTypes()
	return _.includes([SCORECARD_ITEM, INITIATIVE], lineSourceType)
}

function isAggregationSource(lineSourceType) {
	const {AGGREGATION, AGGREGATION_PERCENTAGE} = getLineSourceTypes()
	return _.includes([AGGREGATION, AGGREGATION_PERCENTAGE], lineSourceType)
}

LinesAndBandsDataSource.getMetadata = getMetadata
LinesAndBandsDataSource.getLineSourceTypes = getLineSourceTypes

export default LinesAndBandsDataSource
export { getMetadata }
export { getLineSourceTypes }
LinesAndBandsDataSource.__filename__ = 'charts/edit/analytics/lines-and-bands-data-source.js'
